import type { Abi, Address } from 'viem'

const successCache = new Map<string, Abi>()

/** Blockscout-compatible `module=contract&action=getabi` bases (LUKSO). */
const EXPLORER_ABI_API: Record<number, string> = {
  42: 'https://explorer.execution.mainnet.lukso.network/api',
  4201: 'https://explorer.execution.testnet.lukso.network/api',
}

export type FetchAbiResult = { abi: Abi } | { error: string }

/**
 * Fetch verified contract ABI from the LUKSO explorer (Blockscout API).
 * Browser-safe: explorer returns `Access-Control-Allow-Origin: *`.
 */
export async function fetchVerifiedContractAbi(chainId: number, address: Address): Promise<FetchAbiResult> {
  const cacheKey = `${chainId}:${address.toLowerCase()}`
  const cached = successCache.get(cacheKey)
  if (cached) return { abi: cached }

  const base = EXPLORER_ABI_API[chainId]
  if (!base) {
    return { error: `No explorer ABI API configured for chain ${chainId}. Use manual function entry.` }
  }

  const url = `${base}?module=contract&action=getabi&address=${encodeURIComponent(address)}`
  let res: Response
  try {
    res = await fetch(url)
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e)
    return { error: `Network error: ${msg}` }
  }

  if (!res.ok) {
    return { error: `Explorer HTTP ${res.status}` }
  }

  const data = (await res.json()) as {
    status?: string
    message?: string
    result?: string
  }

  const result = data.result
  if (!result || typeof result !== 'string') {
    return { error: data.message || 'Empty ABI response' }
  }

  const trimmed = result.trim()
  if (!trimmed.startsWith('[')) {
    const msg =
      trimmed.toLowerCase().includes('not verified') || trimmed.toLowerCase().includes('contract')
        ? 'Contract not verified on LUKSO explorer (ABI unavailable).'
        : trimmed.length > 160
          ? 'Explorer did not return an ABI array.'
          : trimmed
    return { error: msg }
  }

  try {
    const parsed = JSON.parse(trimmed) as unknown
    if (!Array.isArray(parsed)) {
      return { error: 'Invalid ABI: expected JSON array' }
    }
    const abi = parsed as Abi
    successCache.set(cacheKey, abi)
    return { abi }
  } catch {
    return { error: 'Failed to parse ABI JSON from explorer' }
  }
}

/**
 * Build canonical function signatures for LSP6 AllowedCalls (same format as `computeSelector` input).
 */
export function abiToFunctionSignatures(abi: Abi): string[] {
  const sigs: string[] = []
  for (const item of abi) {
    if (!item || typeof item !== 'object') continue
    if (item.type !== 'function') continue
    if (!('name' in item) || typeof item.name !== 'string' || !item.name) continue
    const inputs = Array.isArray(item.inputs)
      ? item.inputs.map((i: { type?: string }) => i?.type ?? '').join(',')
      : ''
    sigs.push(`${item.name}(${inputs})`)
  }
  return [...new Set(sigs)].sort((a, b) => a.localeCompare(b))
}
